import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Layers, Plus, Trash2, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { invalidateFinancials, useAccounts, useCategories } from '@/lib/queries';
import { localTodayIso, money } from '@/lib/format';
import { Button, ErrorBox, Modal, Spinner, fieldStyles } from './ui';
import s from './BulkRecordModal.module.css';

type Row = {
  id: string;
  date: string;
  merchant: string;
  amount: string;
  direction: 'IN' | 'OUT';
  categoryId: string;
  note: string;
  skipped: boolean;
};
const makeRow = (date = localTodayIso()): Row => ({
  id: crypto.randomUUID(),
  date,
  merchant: '',
  amount: '',
  direction: 'OUT',
  categoryId: '',
  note: '',
  skipped: false,
});
const touched = (row: Row) => !!(row.merchant.trim() || row.amount || row.note.trim());
function issue(row: Row) {
  if (!row.merchant.trim()) return 'Add a merchant or description.';
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(row.date) ||
    row.date < '0001-01-01' ||
    !Number.isFinite(new Date(row.date).getTime()) ||
    new Date(row.date).toISOString().slice(0, 10) !== row.date
  )
    return 'Choose a valid date.';
  if (
    !/^\d+(\.\d{1,2})?$/.test(row.amount) ||
    Number(row.amount) <= 0 ||
    Number(row.amount) > 21474836.47
  )
    return 'Enter an amount above zero, with at most two decimals.';
  return '';
}
export function BulkRecordModal({
  onClose,
  onSaved,
  initialAccountId,
}: {
  onClose: () => void;
  onSaved: (message: string) => void;
  initialAccountId?: string;
}) {
  const { user } = useAuth();
  const currency = user?.currency ?? 'USD';
  const accounts = useAccounts(),
    categories = useCategories(),
    client = useQueryClient();
  const [rows, setRows] = useState<Row[]>(() => [makeRow(), makeRow(), makeRow()]);
  const [accountId, setAccountId] = useState(initialAccountId ?? '');
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [checked, setChecked] = useState(false),
    [discard, setDiscard] = useState(false);
  const attempt = useRef<{ signature: string; batchId: string } | null>(null);
  const saving = useRef(false);
  const activeAccounts = accounts.data?.accounts.filter((account) => !account.archived) ?? [];
  const account = activeAccounts.find((account) => account.id === accountId);
  useEffect(() => {
    if (!accountId && activeAccounts.length) setAccountId(activeAccounts[0]!.id);
  }, [accounts.data, accountId]);
  const included = rows.filter((row) => !row.skipped && touched(row));
  const invalid = included.filter((row) => issue(row));
  const totals = included.reduce(
    (total, row) => {
      if (!issue(row)) total[row.direction] += Math.round(Number(row.amount) * 100);
      return total;
    },
    { IN: 0, OUT: 0 },
  );
  function change(id: string, patch: Partial<Row>) {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    setError('');
  }
  function close() {
    if (saving.current) return;
    if (rows.some(touched)) setDiscard(true);
    else onClose();
  }
  async function save() {
    if (saving.current) return;
    setChecked(true);
    if (!account || !included.length || invalid.length) {
      setError(
        !account
          ? 'Choose an account.'
          : invalid.length
            ? 'Fix the highlighted rows, or skip them before saving.'
            : 'Fill in at least one row.',
      );
      return;
    }
    const body = {
      accountId,
      rows: included.map((row) => ({
        date: row.date,
        merchant: row.merchant.trim(),
        amount: Number(row.amount),
        direction: row.direction,
        categoryId: row.categoryId || null,
        note: row.note.trim(),
      })),
    };
    const signature = JSON.stringify(body);
    if (attempt.current?.signature !== signature)
      attempt.current = { signature, batchId: crypto.randomUUID() };
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ recorded: number }>('/transactions/bulk-record', {
        method: 'POST',
        body: { ...body, batchId: attempt.current.batchId },
      });
      invalidateFinancials(client);
      onSaved(`Recorded ${result.recorded} transactions.`);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'Couldn’t save this batch. Your entries are still here; retry to finish.',
      );
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal title="Bulk record transactions" className={s.modal} onClose={close}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        noValidate
      >
        <div className={s.intro}>
          <div className={s.mark}>
            <Layers size={24} aria-hidden="true" />
          </div>
          <div>
            <h2>A few entries. One save.</h2>
            <p>Add up to 100 transactions. Blank and skipped rows won’t be saved.</p>
          </div>
        </div>
        <div className={s.defaults}>
          <label>
            Record to account
            <select
              aria-label="Bulk account"
              className={fieldStyles.select}
              value={accountId}
              disabled={busy}
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">Choose an account</option>
              {activeAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {currency}
                </option>
              ))}
            </select>
          </label>
          <p>
            Choose categories yourself or let saved merchant rules categorize them. Money in or out
            always follows your selection.
          </p>
        </div>
        {accounts.error || categories.error ? (
          <ErrorBox>Couldn’t load accounts or categories. Close and try again.</ErrorBox>
        ) : null}
        {!accounts.isLoading && !activeAccounts.length ? (
          <ErrorBox>Add an account in Accounts before recording transactions.</ErrorBox>
        ) : null}
        <fieldset disabled={busy} className={s.rows}>
          {rows.map((row, index) => (
            <div
              key={row.id}
              className={`${s.row} ${row.skipped ? s.skipped : ''} ${checked && !row.skipped && touched(row) && issue(row) ? s.invalid : ''}`}
            >
              <div className={s.rowHead}>
                <strong>Transaction {index + 1}</strong>
                <div>
                  <label className={s.skip}>
                    <input
                      type="checkbox"
                      checked={row.skipped}
                      onChange={(event) => change(row.id, { skipped: event.target.checked })}
                    />{' '}
                    Skip
                  </label>
                  <button
                    type="button"
                    aria-label={`Duplicate row ${index + 1}`}
                    title="Duplicate row"
                    disabled={rows.length >= 100}
                    onClick={() =>
                      setRows((current) => [
                        ...current,
                        { ...row, id: crypto.randomUUID(), skipped: false },
                      ])
                    }
                  >
                    <Copy size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove row ${index + 1}`}
                    title="Remove row"
                    onClick={() =>
                      setRows((current) => current.filter((item) => item.id !== row.id))
                    }
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <fieldset disabled={row.skipped} className={s.fields}>
                <label>
                  Date
                  <input
                    type="date"
                    aria-label={`Date ${index + 1}`}
                    className={fieldStyles.input}
                    value={row.date}
                    onChange={(event) => change(row.id, { date: event.target.value })}
                  />
                </label>
                <label className={s.merchant}>
                  Merchant / description
                  <input
                    aria-label={`Merchant ${index + 1}`}
                    className={fieldStyles.input}
                    maxLength={200}
                    placeholder="e.g. Groceries"
                    value={row.merchant}
                    onChange={(event) => change(row.id, { merchant: event.target.value })}
                  />
                </label>
                <label>
                  Money direction
                  <select
                    aria-label={`Direction ${index + 1}`}
                    className={fieldStyles.select}
                    value={row.direction}
                    onChange={(event) =>
                      change(row.id, {
                        direction: event.target.value as Row['direction'],
                        categoryId: '',
                      })
                    }
                  >
                    <option value="OUT">Money out</option>
                    <option value="IN">Money in</option>
                  </select>
                </label>
                <label>
                  Amount {currency}
                  <input
                    inputMode="decimal"
                    aria-label={`Amount ${index + 1}`}
                    className={fieldStyles.input}
                    value={row.amount}
                    placeholder="0.00"
                    onChange={(event) => change(row.id, { amount: event.target.value })}
                  />
                </label>
                <label>
                  Category
                  <select
                    aria-label={`Category ${index + 1}`}
                    className={fieldStyles.select}
                    value={row.categoryId}
                    onChange={(event) => change(row.id, { categoryId: event.target.value })}
                  >
                    <option value="">Use merchant rules</option>
                    {categories.data
                      ?.filter(
                        (category) =>
                          category.kind === (row.direction === 'IN' ? 'INCOME' : 'EXPENSE'),
                      )
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </label>
              </fieldset>
              <details className={s.note}>
                <summary>Note {row.note ? '· added' : '(optional)'}</summary>
                <input
                  disabled={row.skipped}
                  aria-label={`Note ${index + 1}`}
                  className={fieldStyles.input}
                  maxLength={500}
                  value={row.note}
                  onChange={(event) => change(row.id, { note: event.target.value })}
                />
              </details>
              {checked && !row.skipped && touched(row) && issue(row) ? (
                <p className={s.rowError} role="alert">
                  {issue(row)}
                </p>
              ) : null}
            </div>
          ))}
        </fieldset>
        <Button
          disabled={busy || rows.length >= 100}
          onClick={() => setRows((current) => [...current, makeRow(current.at(-1)?.date)])}
        >
          <Plus size={17} aria-hidden="true" /> Add another row
        </Button>
        <div className={s.footer}>
          {error ? <ErrorBox>{error}</ErrorBox> : null}
          <div className={s.summary}>
            <span>
              <ArrowDownLeft size={16} aria-hidden="true" /> In{' '}
              {money(totals.IN, currency, true)}
            </span>
            <span>
              <ArrowUpRight size={16} aria-hidden="true" /> Out{' '}
              {money(totals.OUT, currency, true)}
            </span>
            <span>{rows.filter((row) => row.skipped).length} skipped</span>
          </div>
          <div className={s.actions}>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                busy || !included.length || !account || !!accounts.error || !!categories.error
              }
            >
              {busy ? <Spinner /> : <Layers size={17} aria-hidden="true" />}
              {busy ? 'Saving…' : `Save ${included.length} transactions`}
            </Button>
          </div>
        </div>
      </form>
      {discard ? (
        <Modal title="Discard this batch?" onClose={() => setDiscard(false)}>
          <p>Your unsaved entries will be removed.</p>
          <div className={s.actions}>
            <Button onClick={() => setDiscard(false)}>Keep editing</Button>
            <Button variant="danger" onClick={onClose}>
              Discard entries
            </Button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
